/**
 * useDocSchema — document category/sub-type schema with TTL cache (AC-2.7).
 *
 * Design decisions:
 *  - T4-3: Module-level TTL cache (5 min) keyed by userId.
 *  - T2-4: Cache cleared on user change via useEffect with userId dep.
 *  - T5-34: AbortController on each fetch.
 *  - Fans out sub-type fetches in parallel (categories endpoint returns sub_types inline).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import useAuth from 'src/hooks/useAuth';
import { getDocSchemaCategories } from 'src/services/aiFileUploadV2';
import type { DocCategoryWithSubTypes } from 'src/types/aiFileUploadV2';

const TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  data: DocCategoryWithSubTypes[];
  fetchedAt: number;
}

// Module-level TTL cache keyed by userId (T4-3)
const schemaCache = new Map<string, CacheEntry>();

function isFresh(entry: CacheEntry): boolean {
  return Date.now() - entry.fetchedAt < TTL_MS;
}

export interface UseDocSchemaResult {
  categories: DocCategoryWithSubTypes[];
  loading: boolean;
  error: string | null;
}

export function useDocSchema(): UseDocSchemaResult {
  const { user } = useAuth();
  const userId = String(user?.id ?? 'anonymous');

  const [categories, setCategories] = useState<DocCategoryWithSubTypes[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchSchema = useCallback((uid: string) => {
    // Serve from cache if fresh
    const cached = schemaCache.get(uid);
    if (cached && isFresh(cached)) {
      setCategories(cached.data);
      return;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    setLoading(true);
    setError(null);

    // Fetch categories only — sub-types are loaded on demand when user selects a category
    getDocSchemaCategories(signal)
      .then((data) => {
        schemaCache.set(uid, { data, fetchedAt: Date.now() });
        setCategories(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        const e = err as Error;
        if (e.name === 'CanceledError' || e.name === 'AbortError') {
          return;
        }
        setError(e.message ?? 'Failed to load document schema');
        setLoading(false);
      });
  }, []);

  // T2-4: Clear cache on user change and refetch
  useEffect(() => {
    // When user changes, clear stale cache for previous users
    schemaCache.forEach((_, key) => {
      if (key !== userId) {
        schemaCache.delete(key);
      }
    });
    fetchSchema(userId);
    return () => {
      abortRef.current?.abort();
    };
  }, [userId, fetchSchema]);

  return { categories, loading, error };
}

// Exported for testing TTL behavior
export { schemaCache, TTL_MS };
